import { IconChevronRight } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OtherPeoplesApproachComponent } from '@/components/prospect-module/other-peoples-approach-component';
import { OurApproachComponent } from '@/components/prospect-module/our-approach-component';
import { ProspectViewCurrentComponent } from '@/components/prospect-module/prospect-view-current-component';
import { ProspectWizard } from '@/components/prospect-module/prospect-wizard';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useDocumentTitle } from '@/contexts/document-title-context';

// Helpers for resilient storage
type StoredProgress = {
  currentProspectStep?: number;
  completedSteps?: number[];
  unlockedSteps?: number[];
};

const readProgress = (key: string): StoredProgress | null => {
  try {
    const raw = localStorage.getItem(key) ?? sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as StoredProgress) : null;
  } catch {
    return null;
  }
};

const writeProgress = (
  key: string,
  value: { currentProspectStep: number; completedSteps: number[]; unlockedSteps: number[] }
) => {
  const payload = JSON.stringify(value);
  try {
    localStorage.setItem(key, payload);
  } catch {
    try {
      sessionStorage.setItem(key, payload);
    } catch {}
  }
};

export const ProspectModule: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [currentProspectStep, setCurrentProspectStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [unlockedSteps, setUnlockedSteps] = useState<number[]>([1]);
  const [companyName, setCompanyName] = useState<string>('');
  const [hasHydratedProgress, setHasHydratedProgress] = useState(false);
  const scrollToTop = () => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      // no-op for non-browser envs
    }
  };

  // Load company data when component mounts or companyId changes
  useEffect(() => {
    if (companyId) {
      // Restore progress from storage
      const key = `prospect-progress:${companyId}`;
      const saved = readProgress(key);
      if (saved) {
        if (typeof saved.currentProspectStep === 'number')
          setCurrentProspectStep(saved.currentProspectStep);
        if (Array.isArray(saved.completedSteps)) setCompletedSteps(saved.completedSteps);
        if (Array.isArray(saved.unlockedSteps) && saved.unlockedSteps.length > 0)
          setUnlockedSteps(saved.unlockedSteps);
      }
      setHasHydratedProgress(true);

      // Load company data
      const loadCompanyData = async () => {
        try {
          setIsLoading(true);
          await new Promise((resolve) => setTimeout(resolve, 500));

          const mockCompanyNames: Record<string, string> = {
            '1': 'Acme Corporation',
            '2': 'Global Manufacturing Ltd',
            '3': 'Healthcare Solutions Inc',
          };

          setCompanyName(mockCompanyNames[companyId] || `Company ${companyId}`);
        } catch {
          // Handle error silently or with proper error reporting
        } finally {
          setIsLoading(false);
        }
      };

      loadCompanyData();
    }
  }, [companyId]);

  // Persist progress whenever it changes
  useEffect(() => {
    if (!companyId || !hasHydratedProgress) return;
    writeProgress(`prospect-progress:${companyId}`, {
      currentProspectStep,
      completedSteps,
      unlockedSteps,
    });
  }, [companyId, currentProspectStep, completedSteps, unlockedSteps, hasHydratedProgress]);

  // Mark current step as unlocked (draft) whenever it becomes active
  useEffect(() => {
    setUnlockedSteps((prev) =>
      prev.includes(currentProspectStep) ? prev : [...prev, currentProspectStep]
    );
  }, [currentProspectStep]);

  useDocumentTitle(companyName);

  // If no companyId, redirect to prospect companies list
  if (!companyId) {
    navigate('/prospect-companies');
    return null;
  }

  const handleStepNavigation = (stepId: number) => {
    // Allow navigation if step is unlocked/completed or current
    if (
      unlockedSteps.includes(stepId) ||
      completedSteps.includes(stepId) ||
      stepId === currentProspectStep
    ) {
      setCurrentProspectStep(stepId);
      scrollToTop();
    }
  };

  const renderBreadcrumb = () => {
    const steps = [
      { id: 1, name: 'Setup' },
      { id: 2, name: 'Current State' },
      { id: 3, name: 'Competitive Analysis' },
      { id: 4, name: 'Our Approach' },
    ];

    return (
      <nav aria-label="breadcrumb" className="flex items-center space-x-1 text-sm">
        <button
          type="button"
          onClick={() => navigate('/prospect-companies')}
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          Prospect Companies
        </button>

        <IconChevronRight className="h-4 w-4 text-muted-foreground" />

        <span className="text-foreground font-semibold">{companyName}</span>

        {currentProspectStep > 0 && (
          <>
            <IconChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center space-x-1">
              {steps
                .filter((s) => unlockedSteps.includes(s.id))
                .map((step, index, _arr) => (
                  <div key={step.id} className="flex items-center">
                    {index > 0 && (
                      <IconChevronRight className="h-3 w-3 mx-1 text-muted-foreground" />
                    )}
                    <button
                      type="button"
                      onClick={() => handleStepNavigation(step.id)}
                      aria-current={step.id === currentProspectStep ? 'step' : undefined}
                      className={`px-2 py-1 rounded transition-colors ${
                        step.id === currentProspectStep
                          ? 'bg-primary text-primary-foreground font-medium dark:bg-muted dark:text-foreground dark:inset-ring dark:inset-ring-foreground'
                          : completedSteps.includes(step.id) || unlockedSteps.includes(step.id)
                            ? 'text-foreground/70 hover:text-foreground'
                            : 'text-muted-foreground cursor-default'
                      }`}
                      disabled={
                        step.id !== currentProspectStep &&
                        !unlockedSteps.includes(step.id) &&
                        !completedSteps.includes(step.id)
                      }
                    >
                      {step.name}
                    </button>
                  </div>
                ))}
            </div>
          </>
        )}
      </nav>
    );
  };

  const handleSetupComplete = () => {
    // Mark step 1 as completed and move to next step
    const nextCompleted = completedSteps.includes(1) ? completedSteps : [...completedSteps, 1];
    const nextStep = 2;
    setCompletedSteps(nextCompleted);
    setUnlockedSteps((prev) => (prev.includes(nextStep) ? prev : [...prev, nextStep]));
    setCurrentProspectStep(nextStep);
    scrollToTop();

    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: nextStep,
        completedSteps: nextCompleted,
        unlockedSteps: [...new Set([...unlockedSteps, nextStep])],
      });
    }
  };

  const handleCurrentStateComplete = () => {
    // Mark step 2 as completed and move to next step (Other People's Approach)
    const nextCompleted = completedSteps.includes(2) ? completedSteps : [...completedSteps, 2];
    const nextStep = 3;
    setCompletedSteps(nextCompleted);
    setUnlockedSteps((prev) => (prev.includes(nextStep) ? prev : [...prev, nextStep]));
    setCurrentProspectStep(nextStep);
    scrollToTop();

    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: nextStep,
        completedSteps: nextCompleted,
        unlockedSteps: [...new Set([...unlockedSteps, nextStep])],
      });
    }
  };

  const handleCurrentStatePrevious = () => {
    setCurrentProspectStep(1);
    scrollToTop();
    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: 1,
        completedSteps,
        unlockedSteps,
      });
    }
  };

  const handleOtherPeoplesApproachComplete = () => {
    // Mark step 3 as completed and move to next step (Our Approach)
    const nextCompleted = completedSteps.includes(3) ? completedSteps : [...completedSteps, 3];
    const nextStep = 4;
    setCompletedSteps(nextCompleted);
    setUnlockedSteps((prev) => (prev.includes(nextStep) ? prev : [...prev, nextStep]));
    setCurrentProspectStep(nextStep);
    scrollToTop();

    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: nextStep,
        completedSteps: nextCompleted,
        unlockedSteps: [...new Set([...unlockedSteps, nextStep])],
      });
    }
  };

  const handleOtherPeoplesApproachPrevious = () => {
    // Go back to Current State (step 2)
    setCurrentProspectStep(2);
    scrollToTop();

    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: 2,
        completedSteps,
        unlockedSteps,
      });
    }
  };

  const handleOurApproachComplete = () => {
    // Mark step 4 as completed - final step of the analysis
    const nextCompleted = completedSteps.includes(4) ? completedSteps : [...completedSteps, 4];
    setCompletedSteps(nextCompleted);

    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: 4,
        completedSteps: nextCompleted,
        unlockedSteps,
      });
    }

    // Could show a completion message or redirect to summary
    alert('Analysis Complete! All steps have been finished.');
  };

  const handleOurApproachPrevious = () => {
    // Go back to Other People's Approach (step 3)
    setCurrentProspectStep(3);
    scrollToTop();

    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: 3,
        completedSteps,
        unlockedSteps,
      });
    }
  };

  const handleResetWorkflow = () => {
    // Reset to setup with only step 1 unlocked
    setCurrentProspectStep(1);
    setCompletedSteps([]);
    setUnlockedSteps([1]);
    scrollToTop();
    if (companyId) {
      writeProgress(`prospect-progress:${companyId}`, {
        currentProspectStep: 1,
        completedSteps: [],
        unlockedSteps: [1],
      });
    }
  };

  const getTabValue = (stepId: number): string => {
    switch (stepId) {
      case 1:
        return 'setup';
      case 2:
        return 'current-state';
      case 3:
        return 'competitor-analysis';
      case 4:
        return 'our-approach';
      default:
        return 'overview';
    }
  };

  const getStepFromTab = (tabValue: string): number => {
    switch (tabValue) {
      case 'setup':
        return 1;
      case 'current-state':
        return 2;
      case 'competitor-analysis':
        return 3;
      case 'our-approach':
        return 4;
      default:
        return 0; // overview
    }
  };

  const handleTabChange = (tabValue: string) => {
    const stepId = getStepFromTab(tabValue);
    if (stepId === 0) {
      // Overview tab - don't change current step
      return;
    }
    // Allow tab change if step is completed or current
    if (
      unlockedSteps.includes(stepId) ||
      completedSteps.includes(stepId) ||
      stepId === currentProspectStep
    ) {
      setCurrentProspectStep(stepId);
      scrollToTop();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading prospect data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <div className="px-4 lg:px-8">
          {/* Breadcrumb Navigation */}
          {renderBreadcrumb()}

          {/* Prospect Details Tabs */}
          <Tabs value={getTabValue(currentProspectStep)} onValueChange={handleTabChange}>
            <TabsContent value="setup" className="mt-6 animate-fade-slide-in">
              <ProspectWizard
                companyId={companyId}
                companyName={companyName}
                onComplete={handleSetupComplete}
              />
            </TabsContent>

            <TabsContent value="current-state" className="mt-6 animate-fade-slide-in">
              <ProspectViewCurrentComponent
                companyName={companyName}
                onComplete={handleCurrentStateComplete}
                onPrevious={handleCurrentStatePrevious}
              />
            </TabsContent>

            <TabsContent value="competitor-analysis" className="mt-6 animate-fade-slide-in">
              <OtherPeoplesApproachComponent
                companyName={companyName}
                onNext={handleOtherPeoplesApproachComplete}
                onPrevious={handleOtherPeoplesApproachPrevious}
              />
            </TabsContent>

            <TabsContent value="our-approach" className="mt-6 animate-fade-slide-in">
              <OurApproachComponent
                companyName={companyName}
                onNext={handleOurApproachComplete}
                onPrevious={handleOurApproachPrevious}
                onReset={handleResetWorkflow}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
